import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { CompanyLogo } from "@/components/ui/company-logo";
import { MapPin, Clock, Lock, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslations } from "@/translations";

interface TeaserJobCardProps {
  job: {
    id: string;
    title: string;
    company: {
      name: string;
      logo_url?: string;
    };
    location: string;
    job_type: string;
    created_at: string;
    featured?: boolean;
    is_remote?: boolean;
  };
  showPartial?: boolean;
}

export const TeaserJobCard = ({ job, showPartial = true }: TeaserJobCardProps) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useTranslations(language);

  const getTimeSince = (date: string) => {
    const now = new Date();
    const posted = new Date(date);
    const diffInHours = Math.floor((now.getTime() - posted.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours}h ${t.jobCard.ago}`;
    }
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ${t.jobCard.ago}`;
  };

  const handleSignUp = () => {
    navigate('/auth?step=signup');
  };

  return (
    <Card className={`job-card shadow-card hover:shadow-primary transition-all duration-300 ${
      job.featured ? 'ring-2 ring-primary shadow-primary border-primary/20' : ''
    }`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <CompanyLogo 
              logoUrl={job.company.logo_url}
              companyName={job.company.name}
              size="md"
            />
            <div className="flex-1">
              <h3 className="text-lg font-semibold leading-tight mb-1 hover:text-primary transition-colors">
                {showPartial ? job.title : "█████ ████████ ████"}
              </h3>
              <p className="text-muted-foreground text-sm font-medium">
                {showPartial ? job.company.name : "████████ ███"}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {job.featured && (
              <Badge variant="default" className="bg-primary text-white">
                <Lock className="w-3 h-3 mr-1" />
                Premium
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="py-3">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs">
              <MapPin className="w-3 h-3 mr-1" />
              {showPartial ? job.location : "████████"}
            </Badge>
            {job.is_remote && showPartial && (
              <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                Remote
              </Badge>
            )}
            <Badge variant="outline" className="text-xs opacity-50">
              <Lock className="w-3 h-3 mr-1" />
              Sign up to see details
            </Badge>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center text-muted-foreground opacity-50">
              <Lock className="w-4 h-4 mr-1" />
              <span>Salary hidden</span>
            </div>
            <div className="flex items-center text-muted-foreground">
              <Clock className="w-4 h-4 mr-1" />
              {getTimeSince(job.created_at)}
            </div>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="pt-3">
        <div className="flex gap-2 w-full">
          <Button variant="outline" size="sm" className="flex-1" onClick={handleSignUp}>
            <Eye className="w-4 h-4 mr-2" />
            View Details
          </Button>
          <Button onClick={handleSignUp} className="flex-1 gradient-primary" size="sm">
            <Lock className="w-4 h-4 mr-1" />
            Sign Up to Apply
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};